import { daysOf } from "./figures";
import { SERIES } from "./series";

/**
 * A count signal's days with data, from the page's first day with data on that source to the source's
 * last day, each with its value; a day with no document is 0, as traffic_lost reads it. Any other day is
 * a gap, and `PeriodCompareChart` estimates a period with gaps from its days with data. Organic sessions
 * are read over the chart window, Search Console's signals over Search Console's own year, which starts
 * before the window (`beforeChart`) and ends on its last day.
 */
const countsOf = (key, page, site) => {
    const earlier = key !== "sessions" && page.beforeChart?.[key] ? page.beforeChart[key] : [];
    const start = earlier.length ? site.searchConsoleChart.start : site.chart.start;
    const last = key === "sessions" ? site.chart.end : site.searchConsoleChart.end;
    const values = [...earlier, ...(page[key] || [])];
    const first = page.firstDayWithData?.[key];
    const counts = new Map();
    if (first == null) return counts;
    daysOf({ start, end: last }).forEach((day, index) => {
        if (day >= first) counts.set(day, values[index] ?? 0);
    });
    return counts;
};

/** A value stored over the window's days, on a chart day; none outside the window. */
const onWindowDay = (values, site, day) => values?.[Math.round((Date.parse(day) - Date.parse(site.chart.start)) / 86400000)] ?? null;

/** The page's daily values of signal `key` over the chart's days, as `PeriodCompareChart` takes them. */
export const dataOf = (key, page, site, chart) => {
    if (key === "position") return daysOf(chart).map((date) => ({ date, value: onWindowDay(page.position, site, date) }));
    const counts = countsOf(key, page, site);
    return daysOf(chart).map((date) => ({ date, value: counts.get(date) ?? null }));
};

/**
 * Every signal as one of `PeriodCompareChart`'s metrics, its data over the chart's days; a signal whose
 * source the workspace has not linked has no value on any day.
 */
export const metricsOf = (page, site, chart) => SERIES.map(({ key, label, tab, available, period, format, lowerIsBetter, unit }) => ({
    key,
    label: tab || label,
    data: available(site) ? dataOf(key, page, site, chart) : daysOf(chart).map((date) => ({ date, value: null })),
    period,
    format,
    lowerIsBetter,
    unit,
}));

/**
 * Each added page shown on the chart as one of `PeriodCompareChart`'s series, its data by signal; a page
 * hidden, still loading or with no file has none.
 */
export const seriesOf = (added, hiddenIds) => added
    .filter(({ id, metrics }) => metrics && !hiddenIds.includes(id))
    .map(({ id, path, color, metrics }) => ({ key: id, label: path, color, data: Object.fromEntries(metrics.map(({ key, data }) => [key, data])) }));
